import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { blockContentSizeZodSchema } from "@@/shared/pages/page-blocks/content/shared/block-content-size";
import { headerPageBlockThemes } from "@@/shared/pages/page-blocks/headers/header-v2/header.page-block.v2.themes";
import type { Percentage} from "@@/shared/percentage";
import { percentageZodSchema } from "@@/shared/percentage";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import {
    asTranslatableValue,
    translatableZodSchema,
    translation,
} from "@@/shared/translations/translation";
import { setWithinPercentageRange } from "@@/utilities/set-within-range";
import { faRectanglePortrait } from "@fortawesome/pro-solid-svg-icons";
import { z } from "zod";
import { pageBlockBaseZodSchema } from "../../page-block-base";
import { pageBlockIdFactory } from "../../page-block-id";
import { pageBlockTitleSizeZodSchema } from "../../shared/page-block-title-size";

const headerPageBlockZodSchema_V2 = pageBlockBaseZodSchema.merge(
    z.object({
        type: z.literal("HEADER_PAGE_BLOCK_V2"),
        theme: z.enum(headerPageBlockThemes),
        height: percentageZodSchema,
        // Titles
        title: translatableZodSchema,
        titleSize: pageBlockTitleSizeZodSchema,
        titleColor: hexColorZodSchema,
        /** `titleVerticalPlacement` defaults to middle when undefined */
        titleVerticalPlacement: z.enum(["top", "bottom", "middle"]).optional(),
        subtitle: translatableZodSchema,
        subtitleSize: pageBlockTitleSizeZodSchema,
        subtitleColor: hexColorZodSchema,

        kicker: translatableZodSchema.optional(),
        kickerColor: hexColorZodSchema.optional(),
        kickerSize: pageBlockTitleSizeZodSchema.optional(),

        textShadow: z.boolean(),
        // height: percentageZodSchema,
        // Call to action
        ctaButtonEnabled: z.boolean(),
        ctaButtonText: translatableZodSchema.optional(),
        ctaButtonUrl: z.string().optional(),
        ctaButtonTextColor: hexColorZodSchema,
        ctaButtonBackgroundColor: hexColorZodSchema,
        ctaButtonBorderColor: hexColorZodSchema,
        // Logo
        logoEnabled: z.boolean(),
        logoSize: blockContentSizeZodSchema,
        customLogoEnabled: z.boolean(),
        customLogo: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
        // Background
        backgroundColor: hexColorZodSchema,
        backgroundImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
    }),
);

type HeaderPageBlock_V2 = z.infer<typeof headerPageBlockZodSchema_V2>;

const headerPageBlockIcon_V2 = faRectanglePortrait;
const _headerPageBlockFactoryParamsZodSchema_V2 =
    headerPageBlockZodSchema_V2.omit({
        _id: true,
        _type: true,
        _createdAt: true,
        type: true,
    });
type FactoryParams = z.infer<typeof _headerPageBlockFactoryParamsZodSchema_V2>;
//  & {
//     height?: Percentage;
// };
/**
 * Factory function for creating a new HeaderPageBlock_V2
 * @param {FactoryParams} params
 * @return {*}  {HeaderPageBlock_V2}
 */
const headerPageBlockFactory_V2 = (
    params: FactoryParams,
): HeaderPageBlock_V2 => {
    return {
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "HEADER_PAGE_BLOCK_V2",
        pageId: params.pageId,
        theme: params.theme ?? "default",
        textShadow: params.textShadow ?? false,
        // height: params.height
        //     ? setWithinPercentageRange(params.height)
        //     : (0.45 as Percentage),
        // Logo
        logoEnabled: params.logoEnabled ?? false,
        logoSize: params.logoSize ?? "M",
        customLogo: params.customLogo || undefined,
        customLogoEnabled: params.customLogoEnabled ?? false,
        // Title
        title: asTranslatableValue(
            params.title || translation({ sv: "Rubrik", en: "Title" }),
        ),
        titleSize: params.titleSize ?? "M",
        titleColor: params.titleColor,
        textColor: undefined,
        titleVerticalPlacement: params.titleVerticalPlacement ?? "middle",
        // Subtitle
        subtitle: asTranslatableValue(params.subtitle),
        subtitleSize: params.subtitleSize ?? "M",
        subtitleColor: params.subtitleColor,

        // Kicker
        kicker: asTranslatableValue(params.kicker),
        kickerColor: params.kickerColor,
        kickerSize: params.kickerSize ?? "M",

        // Call to action
        ctaButtonEnabled: params.ctaButtonEnabled ?? false,
        ctaButtonText: asTranslatableValue(params.ctaButtonText),
        ctaButtonBackgroundColor: params.ctaButtonBackgroundColor,
        ctaButtonBorderColor: params.ctaButtonBorderColor,
        ctaButtonTextColor: params.ctaButtonTextColor,
        ctaButtonUrl: params.ctaButtonUrl || "",
        // Background
        backgroundColor: params.backgroundColor,
        backgroundImage: params.backgroundImage,
        accentColor: params.accentColor,
        height: params.height
            ? setWithinPercentageRange(params.height)
            : (0.8 as Percentage),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isHeaderPageBlock_V2 = (input: any): input is HeaderPageBlock_V2 => {
    return (
        input._type === "PAGE_BLOCK" && input.type === "HEADER_PAGE_BLOCK_V2"
    );
};

export {
    headerPageBlockFactory_V2,
    headerPageBlockIcon_V2,
    headerPageBlockZodSchema_V2,
    isHeaderPageBlock_V2,
};
export type { HeaderPageBlock_V2 };
